import { useState, useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useFirmaContext } from '@/contexts/FirmaContext'
import type { Firma, PrimaryContact } from '@/types'
import { Card, CardHeader, CardBody } from '@/components/Card'
import DataRow from '@/components/DataRow'
import EmptyState from '@/components/EmptyState'
import { Pencil, Save, X, Check, User, Mail, Phone, Smartphone } from 'lucide-react'

// ── Editable Field ─────────────────────────────────────────

function EditableField({
  label,
  value,
  field,
  editing,
  draft,
  onChange,
  type = 'text',
}: {
  label: string
  value: string | null
  field: string
  editing: boolean
  draft: Record<string, string>
  onChange: (field: string, value: string) => void
  type?: 'text' | 'email' | 'url' | 'tel'
}) {
  if (!editing) {
    if (!value) return null
    // Spezial-Rendering für E-Mail und Website
    if (type === 'email') {
      return (
        <DataRow label={label}>
          <a href={`mailto:${value}`} className="text-fom-700 hover:text-fom-500 hover:underline font-medium">{value}</a>
        </DataRow>
      )
    }
    if (type === 'url') {
      return (
        <DataRow label={label}>
          <a href={value} target="_blank" rel="noopener noreferrer" className="text-fom-700 hover:text-fom-500 hover:underline font-medium">{value}</a>
        </DataRow>
      )
    }
    return <DataRow label={label}>{value}</DataRow>
  }

  return (
    <DataRow label={label}>
      <input
        type={type}
        value={draft[field] ?? value ?? ''}
        onChange={(e) => onChange(field, e.target.value)}
        className="w-full px-2.5 py-1.5 text-[0.82rem] rounded-lg border border-gray-200 bg-white/80 focus:border-fom-300 focus:ring-2 focus:ring-fom-100 outline-none transition-colors"
      />
    </DataRow>
  )
}

// ── Main Component ─────────────────────────────────────────

interface Props {
  firma: Firma
  primaryContact: PrimaryContact | null
  accountId: string
}

export default function FirmaStammdaten({ firma, primaryContact, accountId }: Props) {
  const { canWrite } = useFirmaContext()
  const queryClient = useQueryClient()

  // Edit-State pro Sektion
  const [editingKontakt, setEditingKontakt] = useState(false)
  const [editingAdresse, setEditingAdresse] = useState(false)
  const [editingReAdresse, setEditingReAdresse] = useState(false)

  // Draft-Daten (nur geänderte Felder)
  const [draft, setDraft] = useState<Record<string, string>>({})

  const onChange = useCallback((field: string, value: string) => {
    setDraft(prev => ({ ...prev, [field]: value }))
  }, [])

  // Mutation für das Speichern
  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.updateStammdaten(accountId, data),
    onSuccess: async () => {
      // Cache invalidieren und auf frische Daten warten
      await queryClient.refetchQueries({ queryKey: ['firma', accountId] })
    },
  })

  const handleSave = useCallback(async (section: 'kontakt' | 'adresse' | 're_adresse') => {
    // Nur geänderte Felder sammeln die zu dieser Sektion gehören
    const sectionFields: Record<string, string[]> = {
      kontakt: ['email', 'telefon', 'telefon2', 'fax', 'website'],
      adresse: ['strasse', 'hausnummer', 'adresszusatz', 'plz', 'stadt', 'bundesland', 'land'],
      re_adresse: ['re_strasse', 're_hausnummer', 're_plz', 're_stadt', 're_land'],
    }

    const fields = sectionFields[section] || []
    const changes: Record<string, string> = {}
    for (const f of fields) {
      if (f in draft) {
        changes[f] = draft[f]
      }
    }

    if (Object.keys(changes).length === 0) {
      // Nichts geändert → einfach Edit-Modus beenden
      if (section === 'kontakt') setEditingKontakt(false)
      if (section === 'adresse') setEditingAdresse(false)
      if (section === 're_adresse') setEditingReAdresse(false)
      return
    }

    try {
      await mutation.mutateAsync(changes)
      // Draft für diese Felder aufräumen
      setDraft(prev => {
        const next = { ...prev }
        for (const f of fields) delete next[f]
        return next
      })
      if (section === 'kontakt') setEditingKontakt(false)
      if (section === 'adresse') setEditingAdresse(false)
      if (section === 're_adresse') setEditingReAdresse(false)
    } catch {
      // Fehler wird durch mutation.error angezeigt
    }
  }, [draft, mutation, accountId])

  const handleCancel = useCallback((section: 'kontakt' | 'adresse' | 're_adresse') => {
    const sectionFields: Record<string, string[]> = {
      kontakt: ['email', 'telefon', 'telefon2', 'fax', 'website'],
      adresse: ['strasse', 'hausnummer', 'adresszusatz', 'plz', 'stadt', 'bundesland', 'land'],
      re_adresse: ['re_strasse', 're_hausnummer', 're_plz', 're_stadt', 're_land'],
    }
    const fields = sectionFields[section] || []
    setDraft(prev => {
      const next = { ...prev }
      for (const f of fields) delete next[f]
      return next
    })
    if (section === 'kontakt') setEditingKontakt(false)
    if (section === 'adresse') setEditingAdresse(false)
    if (section === 're_adresse') setEditingReAdresse(false)
  }, [])

  return (
    <div className="space-y-4">
      {/* Erfolgs-/Fehler-Meldung */}
      {mutation.isSuccess && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-[0.82rem]">
          <Check size={15} /> Änderungen erfolgreich gespeichert
        </div>
      )}
      {mutation.isError && (
        <div className="px-4 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-[0.82rem]">
          Fehler: {mutation.error instanceof Error ? mutation.error.message : 'Speichern fehlgeschlagen'}
        </div>
      )}

      {/* ── Hauptansprechpartner (readonly) ──────────── */}
      {primaryContact && (
        <div className="bg-white border border-gray-200 rounded-lg px-5 py-4 flex flex-wrap items-center gap-x-8 gap-y-3">
          <div className="flex items-center gap-3 min-w-[200px]">
            <div className="w-9 h-9 rounded-full bg-fom-50 text-fom-600 flex items-center justify-center flex-none">
              <User size={16} />
            </div>
            <div>
              <div className="text-[0.82rem] font-semibold text-gray-900 leading-tight">
                {primaryContact.fullname || '–'}
              </div>
              {primaryContact.jobtitle && (
                <div className="text-[0.72rem] text-gray-500">{primaryContact.jobtitle}</div>
              )}
            </div>
          </div>
          {primaryContact.emailaddress1 && (
            <a href={`mailto:${primaryContact.emailaddress1}`} className="inline-flex items-center gap-1.5 text-[0.78rem] text-fom-700 hover:text-fom-500 hover:underline font-medium no-underline">
              <Mail size={13} className="text-gray-400" />
              {primaryContact.emailaddress1}
            </a>
          )}
          {primaryContact.telephone1 && (
            <span className="inline-flex items-center gap-1.5 text-[0.78rem] text-gray-700">
              <Phone size={13} className="text-gray-400" />
              {primaryContact.telephone1}
            </span>
          )}
          {primaryContact.mobilephone && (
            <span className="inline-flex items-center gap-1.5 text-[0.78rem] text-gray-700">
              <Smartphone size={13} className="text-gray-400" />
              {primaryContact.mobilephone}
            </span>
          )}
          <span className="text-[0.62rem] text-gray-400 uppercase tracking-widest font-semibold ml-auto max-md:ml-0">
            Hauptansprechpartner
          </span>
        </div>
      )}

      {/* ── Kontaktdaten ─────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between w-full">
            <span>Kontaktdaten</span>
            {canWrite('stammdaten') && !editingKontakt && (
              <button
                onClick={() => setEditingKontakt(true)}
                className="flex items-center gap-1.5 text-[0.72rem] font-medium text-fom-600 hover:text-fom-700 transition-colors"
              >
                <Pencil size={13} /> Bearbeiten
              </button>
            )}
            {editingKontakt && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleCancel('kontakt')}
                  className="flex items-center gap-1 text-[0.72rem] font-medium text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X size={13} /> Abbrechen
                </button>
                <button
                  onClick={() => handleSave('kontakt')}
                  disabled={mutation.isPending}
                  className="flex items-center gap-1 text-[0.72rem] font-semibold text-white bg-fom-600 hover:bg-fom-700 px-3 py-1 rounded-md transition-colors disabled:opacity-50"
                >
                  <Save size={13} /> {mutation.isPending ? 'Speichern…' : 'Speichern'}
                </button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardBody>
          {editingKontakt || firma.telefon || firma.email || firma.website ? (
            <>
              <EditableField label="Telefon" value={firma.telefon} field="telefon" editing={editingKontakt} draft={draft} onChange={onChange} type="tel" />
              <EditableField label="Telefon 2" value={firma.telefon2} field="telefon2" editing={editingKontakt} draft={draft} onChange={onChange} type="tel" />
              <EditableField label="Fax" value={firma.fax} field="fax" editing={editingKontakt} draft={draft} onChange={onChange} type="tel" />
              <EditableField label="E-Mail" value={firma.email} field="email" editing={editingKontakt} draft={draft} onChange={onChange} type="email" />
              <EditableField label="Website" value={firma.website} field="website" editing={editingKontakt} draft={draft} onChange={onChange} type="url" />
            </>
          ) : (
            <EmptyState message="Keine Kontaktdaten hinterlegt" />
          )}
        </CardBody>
      </Card>

      {/* ── Firmensitz ───────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between w-full">
            <span>Firmensitz</span>
            {canWrite('stammdaten') && !editingAdresse && (
              <button
                onClick={() => setEditingAdresse(true)}
                className="flex items-center gap-1.5 text-[0.72rem] font-medium text-fom-600 hover:text-fom-700 transition-colors"
              >
                <Pencil size={13} /> Bearbeiten
              </button>
            )}
            {editingAdresse && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleCancel('adresse')}
                  className="flex items-center gap-1 text-[0.72rem] font-medium text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X size={13} /> Abbrechen
                </button>
                <button
                  onClick={() => handleSave('adresse')}
                  disabled={mutation.isPending}
                  className="flex items-center gap-1 text-[0.72rem] font-semibold text-white bg-fom-600 hover:bg-fom-700 px-3 py-1 rounded-md transition-colors disabled:opacity-50"
                >
                  <Save size={13} /> {mutation.isPending ? 'Speichern…' : 'Speichern'}
                </button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardBody>
          {editingAdresse || firma.strasse || firma.stadt ? (
            <>
              <EditableField label="Straße" value={firma.strasse} field="strasse" editing={editingAdresse} draft={draft} onChange={onChange} />
              <EditableField label="Hausnummer" value={firma.hausnummer} field="hausnummer" editing={editingAdresse} draft={draft} onChange={onChange} />
              <EditableField label="Adresszusatz" value={firma.adresszusatz} field="adresszusatz" editing={editingAdresse} draft={draft} onChange={onChange} />
              <EditableField label="PLZ" value={firma.plz} field="plz" editing={editingAdresse} draft={draft} onChange={onChange} />
              <EditableField label="Stadt" value={firma.stadt} field="stadt" editing={editingAdresse} draft={draft} onChange={onChange} />
              <EditableField label="Bundesland" value={firma.bundesland} field="bundesland" editing={editingAdresse} draft={draft} onChange={onChange} />
              <EditableField label="Land" value={firma.land} field="land" editing={editingAdresse} draft={draft} onChange={onChange} />
            </>
          ) : (
            <EmptyState message="Keine Adresse hinterlegt" />
          )}
        </CardBody>
      </Card>

      {/* ── Rechnungsadresse ─────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between w-full">
            <span>Rechnungsadresse</span>
            {canWrite('stammdaten') && !editingReAdresse && (
              <button
                onClick={() => setEditingReAdresse(true)}
                className="flex items-center gap-1.5 text-[0.72rem] font-medium text-fom-600 hover:text-fom-700 transition-colors"
              >
                <Pencil size={13} /> Bearbeiten
              </button>
            )}
            {editingReAdresse && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleCancel('re_adresse')}
                  className="flex items-center gap-1 text-[0.72rem] font-medium text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X size={13} /> Abbrechen
                </button>
                <button
                  onClick={() => handleSave('re_adresse')}
                  disabled={mutation.isPending}
                  className="flex items-center gap-1 text-[0.72rem] font-semibold text-white bg-fom-600 hover:bg-fom-700 px-3 py-1 rounded-md transition-colors disabled:opacity-50"
                >
                  <Save size={13} /> {mutation.isPending ? 'Speichern…' : 'Speichern'}
                </button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardBody>
          {editingReAdresse || firma.re_strasse || firma.re_stadt ? (
            <>
              <EditableField label="Straße" value={firma.re_strasse} field="re_strasse" editing={editingReAdresse} draft={draft} onChange={onChange} />
              <EditableField label="Hausnummer" value={firma.re_hausnummer} field="re_hausnummer" editing={editingReAdresse} draft={draft} onChange={onChange} />
              <EditableField label="PLZ" value={firma.re_plz} field="re_plz" editing={editingReAdresse} draft={draft} onChange={onChange} />
              <EditableField label="Stadt" value={firma.re_stadt} field="re_stadt" editing={editingReAdresse} draft={draft} onChange={onChange} />
              <EditableField label="Land" value={firma.re_land} field="re_land" editing={editingReAdresse} draft={draft} onChange={onChange} />
            </>
          ) : (
            <EmptyState message="Keine Rechnungsadresse hinterlegt" />
          )}
        </CardBody>
      </Card>
    </div>
  )
}
